import React from 'react';
import { QRCodeCanvas } from 'qrcode.react';

// Default helpers if not passed via props
const defaultGetOptimizedImageUrl = (url) => {
  if (!url || typeof url !== 'string' || !url.includes("cloudinary.com")) return url;
  if (url.includes("/upload/c_")) return url;
  return url.replace("/upload/", "/upload/c_limit,w_300,q_auto,f_auto/");
};

const defaultParseImages = (imageUrlField) => {
  if (!imageUrlField) return { full: '', zoom: '', other: '', document: '' };
  if (typeof imageUrlField !== 'string') return { full: '', zoom: '', other: '', document: '' };

  if (imageUrlField.startsWith('{') && imageUrlField.endsWith('}')) {
    try {
      const parsed = JSON.parse(imageUrlField);
      return {
        full: parsed.full || '',
        zoom: parsed.zoom || '',
        other: parsed.other || '',
        document: parsed.document || ''
      };
    } catch (e) {
      return { full: imageUrlField, zoom: '', other: '', document: '' };
    }
  }
  return { full: imageUrlField, zoom: '', other: '', document: '' };
};

const defaultGetRecallImages = (round) => {
  if (!round) return [];
  const list = [];
  if (round.recallImageUrl) {
    list.push(round.recallImageUrl);
  }
  if (Array.isArray(round.recallImageUrls)) {
    round.recallImageUrls.forEach(url => {
      if (url && !list.includes(url)) {
        list.push(url);
      }
    });
  }
  return list;
};

const defaultHighlightActionText = (text) => {
  if (!text) return '';
  const parts = text.split(/(รหัส\s*[A-Za-z0-9-]+|โดย\s*[ก-๙a-zA-Z0-9\s]+|รอบที่\s*\d+|เครื่องนึ่ง\s*[ก-๙a-zA-Z0-9-\s]+)/g);
  return parts.map((part, index) => {
    if (part.startsWith('รหัส')) {
      return <span key={index} style={{ color: '#2563eb', fontWeight: 'bold' }}>{part}</span>;
    }
    if (part.startsWith('โดย')) {
      return <span key={index} style={{ color: '#059669', fontWeight: 'bold' }}>{part}</span>;
    }
    if (part.startsWith('รอบที่') || part.startsWith('เครื่องนึ่ง')) {
      return <span key={index} style={{ color: '#d97706', fontWeight: 'bold' }}>{part}</span>;
    }
    return part;
  });
};

const TracePage = ({
  barcodeInputRef,
  handleBlurToFocus,
  traceInput = '',
  setTraceInput,
  setCameraTargetField,
  setIsCameraOpen,
  handleTraceSearch,
  traceError = '',
  traceData = null,
  traceLogs = [],
  itemMaster = [],
  instrumentTags = [],
  parseImages = defaultParseImages,
  getOptimizedImageUrl = defaultGetOptimizedImageUrl,
  getRecallImages = defaultGetRecallImages,
  setZoomedRecallImageUrl,
  highlightActionText = defaultHighlightActionText,
}) => {
  return (
    <div style={{ background: '#fff', padding: '25px', borderRadius: '15px', boxShadow: '0 4px 6px rgba(0,0,0,0.05)' }}>
      <h2 style={{ textAlign: 'center', color: '#2c3e50', marginBottom: '10px' }}>📍 ข้อมูล QR Code </h2>
      <p style={{ textAlign: 'center', color: '#7f8c8d', marginBottom: '25px' }}>กรอกรหัสบาร์โค้ด 13 หลัก เพื่อดูประวัติการเดินทางของเครื่องมือชิ้นนี้</p>

      <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', marginBottom: '30px' }}>
        <input
          type="text"
          ref={barcodeInputRef}
          onBlur={handleBlurToFocus}
          value={traceInput}
          onChange={(e) => setTraceInput && setTraceInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && handleTraceSearch) {
              handleTraceSearch();
            }
          }}
          placeholder="สแกนรหัส 13 หลัก..."
          style={{ flex: 1, maxWidth: '300px', padding: '15px', fontSize: '1.2em', border: '2px solid #3498db', borderRadius: '8px', textAlign: 'center' }}
        />
        <button
          onClick={() => {
            if (setCameraTargetField) setCameraTargetField('trace');
            if (setIsCameraOpen) setIsCameraOpen(true);
          }}
          style={{ background: '#2c3e50', color: '#fff', border: 'none', padding: '0 20px', borderRadius: '8px', fontSize: '1.5em', cursor: 'pointer' }}
          title="เปิดกล้อง"
        >
          📷
        </button>
        <button
          onClick={handleTraceSearch}
          style={{ background: '#3498db', color: '#fff', border: 'none', padding: '0 25px', borderRadius: '8px', fontSize: '1.1em', fontWeight: 'bold', cursor: 'pointer' }}
        >
          🔍 ค้นหา
        </button>
      </div>

      {traceError && (
        <div style={{ background: '#ffebee', color: '#c0392b', padding: '15px', borderRadius: '8px', textAlign: 'center', fontWeight: 'bold', marginBottom: '20px' }}>
          {traceError}
        </div>
      )}

      {/* แสดง Timeline แบบ Flowchart */}
      {(traceData || (traceLogs && traceLogs.length > 0)) && (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '20px 0', background: '#f8f9fa', borderRadius: '12px', border: '1px solid #ecf0f1' }}>

          <h3 style={{ marginBottom: '30px', color: '#2c3e50', textAlign: 'center' }}>
            📦 {traceData?.item_name || 'ไม่ทราบชื่อเครื่องมือ'} <br />
            <span style={{ fontSize: '0.7em', color: '#7f8c8d' }}>(รหัส: {traceInput})</span>
          </h3>

          {/* Node แรกสุด: ข้อมูลบาร์โค้ด / แพ็กซอง (เพิ่มพิกัดและกล่องเป้าหมาย) */}
          {traceData?.details && (() => {
            // ดึงข้อมูลแม่แบบจาก itemMaster เพื่อดูพิกัดจัดเก็บ
            const mItem = (itemMaster || []).find(m => m.id === traceData.master_id);
            const tag = (instrumentTags || []).find(t => String(t.id) === String(traceData.details?.tag_id));

            return (
              <div style={{
                width: '350px',
                background: traceData.status === 'recalled'
                  ? 'linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)'
                  : traceData.status === 'sterile-ready'
                    ? 'linear-gradient(135deg, #2ecc71 0%, #27ae60 100%)'
                    : 'linear-gradient(135deg, #f6d365 0%, #fda085 100%)',
                border: traceData.status === 'recalled'
                  ? '6px solid #c0392b'
                  : traceData.status === 'sterile-ready'
                    ? '6px solid #27ae60'
                    : '6px solid #f39c12',
                borderRadius: '20px',
                padding: '10px',
                boxShadow: traceData.status === 'recalled'
                  ? '0 15px 35px rgba(0,0,0,0.3), 0 0 25px rgba(192, 57, 43, 0.4)'
                  : traceData.status === 'sterile-ready'
                    ? '0 15px 35px rgba(0,0,0,0.3), 0 0 25px rgba(39, 174, 96, 0.4)'
                    : '0 15px 35px rgba(0,0,0,0.3), 0 0 25px rgba(243, 156, 18, 0.4)',
                color: '#2c3e50',
                fontFamily: "'Segoe UI', Tahoma, sans-serif",
                marginBottom: '40px',
                position: 'relative'
              }}>
                {/* Inside Card body */}
                <div style={{
                  background: traceData.status === 'recalled'
                    ? '#ffebee'
                    : traceData.status === 'sterile-ready'
                      ? '#e8f5e9'
                      : '#fcfcf4',
                  border: traceData.status === 'recalled'
                    ? '2px solid #c0392b'
                    : traceData.status === 'sterile-ready'
                      ? '2px solid #27ae60'
                      : '2px solid #7f8c8d',
                  borderRadius: '12px',
                  padding: '12px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '10px',
                  minHeight: '420px',
                  boxShadow: 'inset 0 0 15px rgba(0,0,0,0.05)'
                }}>
                  {/* Header: Name and HP/Type */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '2px solid #7f8c8d', paddingBottom: '6px' }}>
                    <span style={{ fontWeight: '900', fontSize: '1.1em', letterSpacing: '0.5px', color: '#1a252f' }}>
                      {traceData.item_name ? (traceData.item_name.length > 20 ? traceData.item_name.slice(0, 18) + '...' : traceData.item_name) : 'ไม่ทราบชื่อ'}
                    </span>
                    <span style={{ fontWeight: 'bold', fontSize: '1.05em', color: '#c0392b', display: 'flex', alignItems: 'center', gap: '2px' }}>
                      {traceData.status === 'sterile-ready' ? '⚡ Sterile' : '☣️ Non-Sterile'}
                    </span>
                  </div>

                  {/* Image Window (Pokemon style) */}
                  <div style={{
                    border: '3px solid #bdc3c7',
                    borderRadius: '6px',
                    background: '#fff',
                    padding: '6px',
                    textAlign: 'center',
                    boxShadow: '0 4px 6px rgba(0,0,0,0.06), inset 0 0 10px rgba(0,0,0,0.05)',
                    position: 'relative'
                  }}>
                    {mItem && mItem.image_url ? (() => {
                      const { full, zoom } = parseImages(mItem.image_url);
                      return (
                        <div style={{ display: 'flex', width: '100%', height: '140px', alignItems: 'center', position: 'relative' }}>
                          <img
                            src={getOptimizedImageUrl(full)}
                            alt={traceData.item_name}
                            style={{
                              width: zoom ? '70%' : '100%',
                              height: '100%',
                              objectFit: 'contain',
                              borderRadius: '4px',
                              display: 'block',
                              marginRight: zoom ? 'auto' : '0'
                            }}
                          />
                          {zoom && (
                            <img
                              src={getOptimizedImageUrl(zoom)}
                              alt={`${traceData.item_name} zoom`}
                              style={{
                                position: 'absolute',
                                bottom: '0px',
                                right: '0px',
                                width: '75px',
                                height: '75px',
                                objectFit: 'contain',
                                border: '3px solid #bdc3c7',
                                borderRadius: '8px',
                                background: '#fff',
                                boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
                                zIndex: 10
                              }}
                            />
                          )}
                        </div>
                      );
                    }) : tag && tag.image_url ? (
                      <img
                        src={getOptimizedImageUrl(tag.image_url)}
                        alt={tag.name}
                        style={{ width: '100%', height: '140px', objectFit: 'contain', borderRadius: '4px' }}
                      />
                    ) : (
                      <div style={{ height: '140px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '3em', background: '#eceff1', borderRadius: '4px', color: '#b0bec5' }}>
                        🛠️
                      </div>
                    )}

                    {/* RECALLED (Biohazard) Overlay */}
                    {traceData.status === 'recalled' && (
                      <div style={{
                        position: 'absolute',
                        top: '8px',
                        right: '8px',
                        background: '#ffffff',
                        border: '1.5px solid #c0392b',
                        padding: '4px',
                        borderRadius: '6px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '1px 1px 4px rgba(0,0,0,0.15)',
                        zIndex: 10
                      }}>
                        <svg width="46" height="46" viewBox="0 0 576 512" style={{ display: 'block', fill: '#c0392b' }}>
                          <path d="M287.9 112c18.6 0 36.2 3.8 52.8 9.6 13.3-10.3 23.6-24.3 29.5-40.7-25.2-10.9-53-17-82.2-17-29.1 0-56.9 6-82.1 16.9 5.9 16.4 16.2 30.4 29.5 40.7 16.5-5.7 34-9.5 52.5-9.5zM163.6 438.7c12-11.8 20.4-26.4 24.5-42.4-32.9-26.4-54.8-65.3-58.9-109.6-8.5-2.8-17.2-4.6-26.4-4.6-7.6 0-15.2 1-22.5 3.1 4.1 62.8 35.8 118 83.3 153.5zm224.2-42.6c4.1 16 12.5 30.7 24.5 42.5 47.4-35.5 79.1-90.7 83-153.5-7.2-2-14.7-3-22.2-3-9.2 0-18 1.9-26.6 4.7-4.1 44.2-26 82.9-58.7 109.3zm113.5-205c-17.6-10.4-36.3-16.6-55.3-19.9 6-17.7 10-36.4 10-56.2 0-41-14.5-80.8-41-112.2-2.5-3-6.6-3.7-10-1.8-3.3 1.9-4.8 6-3.6 9.7 4.5 13.8 6.6 26.3 6.6 38.5 0 67.8-53.8 122.9-120 122.9S168 117 168 49.2c0-12.1 2.2-24.7 6.6-38.5 1.2-3.7-.3-7.8-3.6-9.7-3.4-1.9-7.5-1.2-10 1.8C134.6 34.2 120 74 120 115c0 19.8 3.9 38.5 10 56.2-18.9 3.3-37.7 9.5-55.3 19.9-34.6 20.5-61 53.3-74.3 92.4-1.3 3.7.2 7.7 3.5 9.8 3.3 2 7.5 1.3 10-1.6 9.4-10.8 19-19.1 29.2-25.1 57.3-33.9 130.8-13.7 163.9 45 33.1 58.7 13.4 134-43.9 167.9-10.2 6.1-22 10.4-35.8 13.4-3.7.8-6.4 4.2-6.4 8.1.1 4 2.7 7.3 6.5 8 39.7 7.8 80.6.8 115.2-19.7 18-10.6 32.9-24.5 45.3-40.1 12.4 15.6 27.3 29.5 45.3 40.1 34.6 20.5 75.5 27.5 115.2 19.7 3.8-.7 6.4-4 6.5-8 0-3.9-2.6-7.3-6.4-8.1-13.9-2.9-25.6-7.3-35.8-13.4-57.3-33.9-77-109.2-43.9-167.9s106.6-78.9 163.9-45c10.2 6.1 19.8 14.3 29.2 25.1 2.5 2.9 6.7 3.6 10 1.6s4.8-6.1 3.5-9.8c-13.1-39.1-39.5-72-74.1-92.4zm-213.4 129c-26.5 0-48-21.5-48-48s21.5-48 48-48 48 21.5 48 48-21.5 48-48 48z" />
                        </svg>
                      </div>
                    )}

                    {/* NON-STERILE Overlay */}
                    {traceData.status !== 'sterile-ready' && traceData.status !== 'recalled' && (
                      <div style={{
                        position: 'absolute',
                        top: '12px',
                        right: '12px',
                        background: '#ffffff',
                        border: '1.5px solid #000000',
                        padding: '2px 8px',
                        fontSize: '0.72em',
                        fontWeight: 'bold',
                        color: '#000000',
                        letterSpacing: '0.5px',
                        fontFamily: 'Arial, sans-serif',
                        boxShadow: '1px 1px 3px rgba(0,0,0,0.15)',
                        zIndex: 10
                      }}>
                        NON-STERILE
                      </div>
                    )}

                    {/* STERILE Overlay */}
                    {traceData.status === 'sterile-ready' && (() => {
                      const isEO = traceData.details?.machine && (
                        traceData.details.machine.toUpperCase().includes('EO') ||
                        traceData.details.machine.toLowerCase().includes('ethylene') ||
                        traceData.details.machine.toLowerCase().includes('gas')
                      );

                      return (
                        <div style={{
                          position: 'absolute',
                          top: '12px',
                          right: '12px',
                          background: '#ffffff',
                          border: '1.5px solid #000000',
                          display: 'flex',
                          alignItems: 'center',
                          padding: '2px 0',
                          fontSize: '0.72em',
                          fontWeight: 'bold',
                          color: '#000000',
                          boxShadow: '1px 1px 3px rgba(0,0,0,0.15)',
                          fontFamily: 'Arial, sans-serif',
                          zIndex: 10
                        }}>
                          <div style={{ padding: '0 8px', letterSpacing: '0.5px' }}>STERILE</div>
                          <div style={{ width: '1px', height: '14px', background: '#000000' }} />
                          {isEO ? (
                            <div style={{ padding: '0 8px', letterSpacing: '0.5px' }}>EO</div>
                          ) : (
                            <div style={{ padding: '0 6px', display: 'flex', alignItems: 'center' }}>
                              <svg width="10" height="18" viewBox="0 0 12 20" style={{ display: 'block' }}>
                                <path
                                  d="M6,1.5 C4.9,1.5 4,2.4 4,3.5 L4,11 C2.8,11.9 2,13.3 2,15 C2,17.2 3.8,19 6,19 C8.2,19 10,17.2 10,15 C10,13.3 9.2,11.9 8,11 L8,3.5 C8,2.4 7.1,1.5 6,1.5 Z"
                                  fill="#ffffff"
                                  stroke="#000000"
                                  strokeWidth="1.2"
                                  strokeLinejoin="round"
                                />
                                <circle cx="6" cy="15" r="2" fill="#000000" />
                                <line x1="6" y1="9" x2="6" y2="14" stroke="#000000" strokeWidth="1.2" strokeLinecap="round" />
                              </svg>
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>

                  {/* Card Stats (Storage location if any) */}
                  {mItem && (mItem.cabinet || mItem.shelf || mItem.row) && (
                    <div style={{ background: '#e3f2fd', padding: '6px 10px', borderRadius: '6px', fontSize: '0.82em', border: '1px solid #90caf9', color: '#1e88e5', display: 'flex', alignItems: 'center', gap: '5px' }}>
                      <span>📍</span>
                      <span><strong>พิกัดจัดเก็บ:</strong> ตู้ {mItem.cabinet || '-'} | ชั้น {mItem.shelf || '-'} | แถว {mItem.row || '-'}</span>
                    </div>
                  )}

                  {/* Pokemon Attacks/Details */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '0.85em', color: '#2c3e50', flex: 1, marginTop: '5px' }}>

                    {/* Attack 1: Pack details */}
                    <div style={{ borderBottom: '1px dashed #bdc3c7', paddingBottom: '6px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 'bold' }}>
                        <span style={{ fontSize: '1.2em' }}>⚙️</span>
                        <span>รายละเอียดเครื่องมือ</span>
                      </div>
                      <div style={{ paddingLeft: '22px', fontSize: '0.92em', color: '#555', lineHeight: '1.4' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap', marginBottom: '3px' }}>
                          <span>•</span>
                          <span style={{ display: 'inline-flex', alignItems: 'center' }} title="วันผลิต (Manufacturing Date)">
                            <svg width="15" height="15" viewBox="0 0 28 28" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#2c3e50', marginRight: '4px' }}>
                              <path d="M 2,24 L 2,14 L 6,10 L 10,14 L 14,10 L 18,14 L 22,10 L 22,4 L 26,4 L 26,24 Z" fill="rgba(44, 62, 80, 0.15)" />
                            </svg>
                          </span>
                          <span>{traceData.details.mfgDate}</span>
                          <span style={{ color: '#cbd5e1', margin: '0 6px' }}>/</span>
                          <span style={{ fontSize: '1.1em', display: 'inline-flex', alignItems: 'center' }} title="วันหมดอายุ (Expiration Date)">⌛</span>
                          <span style={{ color: '#d35400', fontWeight: 'bold' }}>{traceData.details.expDate}</span>
                        </div>
                        {getRecallImages(traceData.details).length > 0 && (
                          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '8px' }}>
                            {getRecallImages(traceData.details).map((imgUrl, idx) => (
                              <img
                                key={idx}
                                src={imgUrl}
                                alt={`ภาพถ่ายวัสดุมีปัญหา ${idx + 1}`}
                                style={{
                                  maxWidth: '120px',
                                  maxHeight: '120px',
                                  borderRadius: '6px',
                                  border: '1px solid #fee2e2',
                                  objectFit: 'cover',
                                  cursor: 'pointer',
                                  boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                                }}
                                onClick={() => setZoomedRecallImageUrl && setZoomedRecallImageUrl(imgUrl)}
                              />
                            ))}
                          </div>
                        )}
                        <div>• <strong>ผู้เตรียมเครื่องมือ:</strong> {traceData.details.preparedBy || '-'}</div>
                        <div>• <strong>ผู้ตรวจเครื่องมือ:</strong> {traceData.details.checkedBy || '-'}</div>
                        <div>• <strong>รูปแบบบรรจุ:</strong> {traceData.details.packaging}</div>
                      </div>
                    </div>

                    {/* Attack 2: Autoclave details & QR code side-by-side */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px' }}>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 'bold' }}>
                          <span style={{ fontSize: '1.2em' }}>🔥</span>
                          <span>STERILIZATION DATA</span>
                        </div>
                        <div style={{ paddingLeft: '22px', fontSize: '0.92em', color: '#555', lineHeight: '1.4' }}>
                          <div>• <strong>เครื่อง:</strong> {traceData.details.machine || '-'}</div>
                          <div>• <strong>รอบ:</strong> รอบที่ {traceData.details.cycle || '-'}</div>
                        </div>
                      </div>
                      <div style={{ background: '#ffffff', padding: '2px', border: '1px solid #7f8c8d', borderRadius: '3px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <QRCodeCanvas value={traceData.unique_barcode || ''} size={72} level="M" />
                      </div>
                    </div>

                  </div>

                  {/* Card Footer */}
                  <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', fontSize: '0.72em', color: '#7f8c8d', marginTop: 'auto' }}>
                    <span>กลุ่มงานทันตกรรมโรงพยาบาลฝาง</span>
                  </div>

                </div>

                {/* Recalled diagonal warning stamp */}
                {traceData.status === 'recalled' && (
                  <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%) rotate(-12deg)',
                    background: 'rgba(255, 255, 255, 0.75)',
                    border: '4px solid rgba(192, 57, 43, 0.75)',
                    padding: '10px 15px',
                    color: 'rgba(192, 57, 43, 0.85)',
                    fontWeight: '900',
                    fontSize: '1.05em',
                    textAlign: 'center',
                    borderRadius: '8px',
                    boxShadow: '0 8px 20px rgba(0,0,0,0.2)',
                    whiteSpace: 'nowrap',
                    zIndex: 100,
                    pointerEvents: 'none',
                    fontFamily: "'Segoe UI', 'Noto Sans Thai', sans-serif"
                  }}>
                    เครื่องมือมีปัญหา ส่งคืนเพื่อตรวจสอบ
                  </div>
                )}
              </div>
            );
          })()}

          {/* Timeline vertical container */}
          <div className="trace-timeline-container">
            {/* Vertical Line */}
            <div className="trace-timeline-line" />

            {(traceLogs || []).map((log) => {
              let typeClass = 'default';
              let icon = '📍';
              let badgeText = 'บันทึกประวัติ';

              const warehouse = log.warehouse || '';

              if (warehouse.includes('sterile')) {
                typeClass = 'sterile';
                icon = '🏥';
                badgeText = 'ทำลายเชื้อแล้ว';
              }
              else if (warehouse.includes('ล้าง')) {
                typeClass = 'wash';
                icon = '🧽';
                badgeText = 'ห้องล้างเครื่องมือ';
              }
              else if (warehouse.includes('คลีนิก') || warehouse.includes('ห้อง') || warehouse.includes('PCU') || warehouse.includes('OR')) {
                typeClass = 'dispense';
                icon = '📤';
                badgeText = 'จ่ายออกใช้งาน';
              }

              const logDate = new Date(log.timestamp);
              const timeStr = logDate.toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' });
              const dateStr = logDate.toLocaleDateString('th-TH', { day: 'numeric', month: 'short' });

              return (
                <div key={log.id} className="trace-timeline-item">
                  {/* Time label column */}
                  <div className="trace-timeline-time">
                    <div className="trace-timeline-time-hour">{timeStr}</div>
                    <div className="trace-timeline-time-date">{dateStr}</div>
                  </div>

                  {/* Dot column */}
                  <div className="trace-timeline-dot-container">
                    <div className={`trace-timeline-dot ${typeClass}`} title={warehouse}>
                      {icon}
                    </div>
                  </div>

                  {/* Card details column */}
                  <div className={`trace-timeline-card ${typeClass}`}>
                    <div className="trace-timeline-card-header">
                      <h4 className="trace-timeline-card-title">{warehouse}</h4>
                      <span className={`trace-timeline-badge ${typeClass}`}>
                        {badgeText}
                      </span>
                    </div>
                    <div className="trace-timeline-card-body">
                      {highlightActionText(log.action)}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div style={{
            marginTop: '40px',
            padding: '16px 32px',
            background: traceData?.is_active
              ? 'linear-gradient(135deg, #1e293b 0%, #334155 100%)'
              : 'linear-gradient(135deg, #64748b 0%, #94a3b8 100%)',
            color: '#ffffff',
            borderRadius: '20px',
            fontWeight: '700',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)',
            textAlign: 'center',
            fontFamily: "'Segoe UI', 'Noto Sans Thai', sans-serif"
          }}>
            {traceData?.is_active ? '✨ บาร์โค้ดนี้กำลังทำงานอยู่ในระบบ' : '🛑 สิ้นสุดวงจรการทำงาน (รับคืนเข้าห้องล้าง / ยกเลิกแล้ว)'}
          </div>

        </div>
      )}
    </div>
  );
};

export default TracePage;
